import type { CastPlaybackQueue } from "@/playback/cast/CastController.types";

type QueueActivationState = Pick<
  CastPlaybackQueue,
  "activationIntent" | "id" | "loadKey"
>;

type SelectablePlaybackQueue = Pick<
  CastPlaybackQueue,
  "currentIndex" | "getPosition" | "id" | "loadKey"
> & {
  items: readonly unknown[];
};

/**
 * A direct row selection is a new receiver queue activation. The receiver
 * package exposes next/previous commands but no arbitrary queue jump, so a
 * non-adjacent selection reloads the same queue at the requested item. The
 * fresh load key also rejects late status from the previously playing item.
 */
export function selectPlaybackQueueItem<T extends SelectablePlaybackQueue>(
  queue: T,
  nextIndex: number,
  selectionKey: string
): T | null {
  if (
    nextIndex < 0 ||
    nextIndex >= queue.items.length ||
    nextIndex === queue.currentIndex
  ) {
    return null;
  }
  return {
    ...queue,
    currentIndex: nextIndex,
    getPosition: () => 0,
    loadKey: `${queue.id}:select:${selectionKey}`
  };
}

/**
 * Detached remote playback is preserved only for mini-player re-entry or a
 * repeat publication of the same route activation. A fresh dance selection
 * owns the next index and starts a new playback activation.
 */
export function shouldReattachPlaybackQueue({
  next,
  previous,
  remoteActive,
  screenAttached
}: {
  next: QueueActivationState;
  previous: QueueActivationState | null;
  remoteActive: boolean;
  screenAttached: boolean;
}): boolean {
  if (!remoteActive || previous?.id !== next.id) return false;
  // Navigation can mount the returning watch route before the outgoing route's
  // cleanup marks the controller detached. Explicit mini-player intent is
  // authoritative regardless of that React lifecycle ordering.
  if (next.activationIntent === "reattach") return true;
  return !screenAttached && previous.loadKey === next.loadKey;
}
